import isEqual from 'lodash/isEqual';
import { blockAttributesMapReversed } from '../attributes';
import { BlockOp, IDelta, IOp, ITextAttributes } from '../types';
import { QuilltoCDIFAttributeMapping, StyleAttributes } from './StyleAttributes';
import { ListStyle, TextAreaContent, TextAttributes, TextField, TextInline, TextList, TextListItem } from './types';

interface BlockAttributes {
    blockColor: string;
    blockFontSize: number;
    blockFontFamily: string;
    blockCasing?: string;
    blockLineHeight?: string;
    blockLetterspacing?: string;
    blockFontWeight?: string;
    blockFontStyle?: string;
    blockTextDecoration?: string;
    blockStrokeColor?: string;
    blockStrokeOverprints?: string[];
    blockStrokeThickness?: number;
    blockStrokePosition?: string;
    blockOverprints?: string[];
}

interface ParentAttributes {
    color: string;
    fontSize: number;
    fontFamily: string;
    casing?: string;
    lineHeight?: string;
    letterspacing?: string;
    fontWeight?: string;
    fontStyle?: string;
    textDecoration?: string;
    strokeColor?: string;
    strokeOverprints?: string[];
    strokeThickness?: number;
    strokePosition?: string;
    overprints?: string[];
}

function createFontStyle(attributes: ITextAttributes = {}) {
    const styles = Object
        .keys(attributes)
        // eslint-disable-next-line max-len
        .reduce((acc: string[], attr) => ((QuilltoCDIFAttributeMapping[attr] && attributes[attr] === StyleAttributes[attr]) ? [...acc, QuilltoCDIFAttributeMapping[attr]] : acc), []);

    return styles.length ? styles.join(',') : 'normal';
}

const REQUIRED_ATTRIBUTES = ['color', 'fontFamily', 'fontSize'];

const validateAttribute = (attributes: ITextAttributes) => (attributeName: string) => {
    if (attributeName === 'color') {
        // color is not required if there are overprints or a defined stroke
        const hasOverprints = attributes.overprints != null && attributes.overprints.length !== 0;
        const hasStrokeColor = attributes.strokeColor != null && attributes.strokeColor !== 'none';
        const hasStrokeOverprints = attributes.strokeOverprints != null && attributes.strokeOverprints.length !== 0;
        const hasStroke = (hasStrokeColor || hasStrokeOverprints) && attributes.strokeThickness != null;
        if (hasOverprints || hasStroke) return;
    }
    if (attributes[attributeName] == null) {
        throw Error(`${attributeName} missing from delta`);
    }
};

export function validateRequiredAttributes(attributes: ITextAttributes) {
    REQUIRED_ATTRIBUTES.forEach(validateAttribute(attributes));
}

function convertDeltaFontSize(size: number) {
    return `${size}pt`;
}

function convertBlockAttributes(attributes: Partial<BlockAttributes> = {}) {
    const result: Partial<ParentAttributes> = {};
    Object.keys(attributes).forEach((key) => {
        if (blockAttributesMapReversed[key]) {
            result[blockAttributesMapReversed[key]] = attributes[key];
        }
    });
    return result;
}

function createTextAttributes(attributes: ITextAttributes, colorConverter: (value: string) => string) {
    const result: Partial<TextAttributes> = {};
    // Do some cleanup here by not adding properties to the result where the default value is functionally identical to the property not existing
    if (attributes.color) {
        result.color = colorConverter(attributes.color);
    }
    if (attributes.fontFamily) {
        result.fontFamily = attributes.fontFamily;
    }
    if (attributes.fontSize) {
        result.fontSize = convertDeltaFontSize(attributes.fontSize);
    }
    if (attributes.casing && attributes.casing !== 'default') {
        result.casing = attributes.casing;
    }
    // The default value for line height is font dependent so the only way to remove it is to unset it.
    if (attributes.lineHeight) {
        result.lineHeight = attributes.lineHeight;
    }
    if (attributes.letterspacing && attributes.letterspacing !== '0em') {
        result.letterspacing = attributes.letterspacing;
    }
    // Stroke thickness can be defined in either pt or em. In either case we should not persist it if if the value regardless of unit is 0.
    const hasStrokeColor = attributes.strokeColor && attributes.strokeColor !== 'none';
    const hasStrokeOverprints = attributes.strokeOverprints && attributes.strokeOverprints.length !== 0;
    if (
        attributes.strokeThickness
        && parseFloat(attributes.strokeThickness)
        && (hasStrokeColor || hasStrokeOverprints)
    ) {
        result.stroke = {
            color: hasStrokeColor ? attributes.strokeColor : undefined,
            thickness: attributes.strokeThickness,
            overprints: hasStrokeOverprints ? attributes.strokeOverprints : undefined,
        };

        if (
            attributes.strokePosition
            && attributes.strokePosition !== 'center'
        ) {
            result.stroke.position = attributes.strokePosition;
        }
    }
    if (attributes.overprints && attributes.overprints.length !== 0) {
        result.overprints = attributes.overprints.map((c: string) => colorConverter(c));
    }

    result.fontStyle = createFontStyle(attributes);

    return result;
}

function convertOpToTextField(op: IOp, colorConverter: (value: string) => string, parentAttributes: ParentAttributes): TextField;
function convertOpToTextField(op: IOp, colorConverter: (value: string) => string): TextInline;
function convertOpToTextField(
    op: IOp, colorConverter: (value: string) => string,
    parentAttributes?: ParentAttributes,
): TextField | TextInline {
    // Use attributes that may have been set as "blockAttribute" on an op instead of "attribute" so the parentAttributes wont overwrite them
    const blockAttributes = convertBlockAttributes(op.attributes);
    const rawAttributes = {
        ...parentAttributes,
        ...blockAttributes,
        ...op.attributes,
    };

    if (parentAttributes) {
        validateRequiredAttributes(rawAttributes);
    }

    const textAttributes = createTextAttributes(rawAttributes, colorConverter);

    return {
        content: op.insert,
        ...textAttributes,
    };
}

function addOpsToTextList(
    textList: TextList,
    ops: IOp[],
    depth: number,
    listStyle: ListStyle,
    parentAttributes: ParentAttributes,
    colorConverter: (value: string) => string,
) {
    let content: TextInline[] = ops.map((op) => convertOpToTextField(op, colorConverter, parentAttributes));
    // If the list item has no content, set it to an empty string.
    if (content.length === 0) {
        content = [{
            content: '',
        }];
    }

    const textListItem: TextListItem = {
        type: 'listItem',
        listMarkerStyle: listStyle,
        depth: depth + 1,
        ...(createTextAttributes(parentAttributes, colorConverter)),
        content,
    };

    /**
     * After creating the text list item, many of its attributes and those on the inline items will have been merged (e.g. fontStyle) and others
     * transferred down the list (e.g. color, fontSize). We need to deduplicate matching attributes that will just be inherited by items higher in the list hierarchy.
     *
     * This should not be a performance issue since we only process the attributes of the inline items and the list items once before adding them to the parent list.
     */

    // Dedupe attributes from inline items that are the same as the parent list item.
    textListItem.content.forEach((textInlineItem) => {
        Object.keys(textInlineItem).forEach((attr) => {
            if (isEqual(textInlineItem[attr], textListItem[attr])) {
                // eslint-disable-next-line no-param-reassign
                delete textInlineItem[attr];
            }
        });
    });

    // Dedupe attributes from the text list item that are the same as the parent list.
    Object.keys(textListItem).forEach((attr) => {
        if (isEqual(textListItem[attr], textList[attr])) {
            // eslint-disable-next-line no-param-reassign
            delete textListItem[attr];
        }
    });

    textList.content.push(textListItem);
}

function createTextList(
    ops: IOp[],
    depth: number,
    listStyle: ListStyle,
    parentAttributes: ParentAttributes,
    colorConverter: (value: string) => string,
): TextList {
    const textList: TextList = {
        ...(createTextAttributes(parentAttributes, colorConverter) as TextAttributes),
        type: 'list',
        content: [],
    };

    addOpsToTextList(textList, ops, depth, listStyle, parentAttributes, colorConverter);

    return textList;
}

function convertOpListAttribute(opListAttribute?: string): ListStyle {
    if (opListAttribute === 'bullet') {
        return 'unordered';
    }

    if (opListAttribute === 'ordered') {
        return 'ordered';
    }

    throw new TypeError('Provided attribute is not of op list type: bullet or ordered');
}

const NEWLINE_REGEX = /^\n+$/;

function isBlock(op: IOp): op is BlockOp {
    return NEWLINE_REGEX.test(op.insert);
}

function isTextList(textAreaContent: TextAreaContent | TextInline): textAreaContent is TextList {
    return (textAreaContent as TextList).type === 'list';
}

function isTextField(textAreaContent: TextAreaContent | TextInline): textAreaContent is TextField | TextInline {
    return (textAreaContent as TextField).type === 'inline' || (textAreaContent as TextField).type === undefined;
}

function isListOp(op: IOp) {
    return op.attributes && op.attributes.list;
}

function removeTrailingNewline(textField: TextField) {
    if (textField.content[textField.content.length - 1] === '\n') {
        // eslint-disable-next-line no-param-reassign
        textField.content = textField.content.substring(0, textField.content.length - 1);
    }
}

function blockScan(ops: IOp[], colorConverter: (value: string) => string) {
    let textOps: IOp[] = [];
    const textAreaContents: TextAreaContent[] = [];
    for (let i = 0; i < ops.length; i += 1) {
        const op = ops[i];
        if (isBlock(op)) {
            // block
            const parentAttributes = {
                color: op.attributes.blockColor,
                fontSize: op.attributes.blockFontSize,
                fontFamily: op.attributes.blockFontFamily,
                casing: op.attributes.blockCasing,
                lineHeight: op.attributes.blockLineHeight,
                letterspacing: op.attributes.blockLetterspacing,
                fontWeight: op.attributes.blockFontWeight,
                fontStyle: op.attributes.blockFontStyle,
                textDecoration: op.attributes.blockTextDecoration,
                strokeColor: op.attributes.blockStrokeColor,
                strokeOverprints: op.attributes.blockStrokeOverprints,
                strokeThickness: op.attributes.blockStrokeThickness,
                strokePosition: op.attributes.blockStrokePosition,
                overprints: op.attributes.blockOverprints,
            };

            if (isListOp(op)) {
                // list
                const depth = op.attributes.indent || 0;
                const previousItem = textAreaContents.length !== 0 ? textAreaContents[textAreaContents.length - 1] : undefined;
                const listStyle = convertOpListAttribute(ops[i].attributes!.list);
                if (previousItem && isTextList(previousItem)) {
                    addOpsToTextList(previousItem, textOps, depth, listStyle, parentAttributes, colorConverter);
                } else {
                    // Previous item is a regular text field, so we should remove the trailing newline since lists implicitly prepend a newline
                    if (previousItem && isTextField(previousItem)) {
                        removeTrailingNewline(previousItem);
                    }
                    textAreaContents.push(createTextList(textOps, depth, listStyle, parentAttributes, colorConverter));
                }

                textOps = [];
            } else {
                // paragraph
                if (i === ops.length - 1) {
                    // If this is the last block, we need to take off one of the newlines
                    const newlines = op.insert.split('').slice(1).join('');
                    op.insert = newlines;
                }

                if (textOps.length) {
                    const textFields = textOps.map((textOp) => convertOpToTextField(textOp, colorConverter, parentAttributes));
                    // Append new lines from block
                    textFields[textFields.length - 1].content += op.insert;
                    textAreaContents.push(...textFields);
                    textOps = [];
                } else {
                    // empty paragraph
                    textAreaContents.push(convertOpToTextField({ insert: op.insert, attributes: {} }, colorConverter, parentAttributes));
                }
            }
        } else {
            // text
            textOps.push(op);
        }
    }

    return textAreaContents;
}

/**
 * Split a list block into multiple blocks if it has more than one newline inside.
 * This simplifies the logic for converting to the cimdoc format
 */
function splitLists(ops: IOp[]) {
    const newOps: IOp[] = [];

    for (const op of ops) {
        if (isListOp(op)) {
            newOps.push(...op.insert.split('').map(() => ({ insert: '\n', attributes: op.attributes })));
        } else {
            newOps.push(op);
        }
    }

    return newOps;
}

export function convertDeltaToFields(colorConverter: (value: string) => string, delta: IDelta): TextAreaContent[] {
    const ops = splitLists(delta.ops);
    return blockScan(ops, colorConverter);
}
